import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import type { ComponentType } from "react";
import {
  Button,
  type ButtonTone,
} from "@freightsimple/ui-core/Controls/Button";
import {
  Menu,
  MenuGroup,
  MenuSeparator,
} from "@freightsimple/ui-core/Menu/Menu";

export interface ThreeDotMenuItem {
  name: string;
  subitems?: Array<string> | undefined;
  action?: ((() => void) | ((_: number) => void)) | undefined;
  selectedIndex?: number | undefined;
  disabled?: boolean | undefined;
  disabledExplanation?: string | undefined;
  /** Leading icon for the row. Menus read faster when every row has one — a
   *  half-iconed menu leaves the labels hanging off an inconsistent column. */
  icon?: ComponentType<{ className?: string }> | undefined;
  /** `danger` for destructive rows (Delete). It reddens the whole row, icon
   *  included, which a colour class can't do from out here: the menu paints a
   *  neutral row's icon brand blue and only a non-neutral tone skips that. */
  tone?: ButtonTone | undefined;
}

interface ThreeDotMenuProps {
  items: Array<ThreeDotMenuItem>;
}

// The legacy Cargo three-dot menu's items-array API on ui-core Menu.
// Buttons inside a Menu render as menu items; the menu content portals to
// <body>, so tests click the item globally after opening, not scoped to the
// row (see clickThreeDotMenuItemForName in the cypress suites).
function ThreeDotMenu(props: ThreeDotMenuProps) {
  // Item names are the natural keys; repeated names ("Divider" especially)
  // get an occurrence suffix so keys stay unique
  const nameCounts = new Map<string, number>();
  const keyedItems = props.items.map((item) => {
    const occurrence = nameCounts.get(item.name) ?? 0;
    nameCounts.set(item.name, occurrence + 1);
    return {
      item,
      itemKey: occurrence === 0 ? item.name : `${item.name}-${occurrence}`,
    };
  });

  return (
    // No `trigger`: ui-core Menu draws its own kebab, which is what hubble has
    // always used. Dashboard used to hand-roll a borderless one — a bare 24px
    // glyph with an optional grey tint — which is why this file had `tone` and
    // `rowTestId` props. The tint had no equivalent on the boxed kebab, and
    // `rowTestId` only fed a `data-rowid` attribute no test ever read; the
    // cypress helpers narrow to the row by its own testid and then look for
    // `three-dot-menu` inside it.
    // `iconColumn` off unless something in this menu actually has an icon —
    // otherwise every label sits 22px right of nothing. Subitems count: a
    // group's selected row draws a check, and that check lives in the same
    // column as the top-level icons.
    <Menu
      data-testid="three-dot-menu"
      iconColumn={props.items.some(
        (item) => item.icon !== undefined || item.subitems !== undefined,
      )}
    >
      {keyedItems.map(({ item, itemKey }) => {
        // Legacy Cargo convention: an item literally named "Divider" renders
        // as a separator rule, not a menu entry.
        if (item.name === "Divider") {
          return <MenuSeparator key={itemKey} />;
        }
        if (item.subitems !== undefined) {
          return (
            // MenuGroup gives the titled group Radix's Group + Label semantics,
            // which the hand-rolled heading div never had.
            <MenuGroup key={itemKey} label={item.name}>
              {item.subitems.map((subitem, subitemIndex) => (
                <Button
                  key={subitem}
                  {...(subitemIndex === item.selectedIndex
                    ? { icon: CheckCircleIcon }
                    : {})}
                  onClick={() =>
                    (item.action as (_: number) => void)?.(subitemIndex)
                  }
                >
                  {subitem}
                </Button>
              ))}
            </MenuGroup>
          );
        }

        if (item.disabled) {
          return (
            <Button
              key={itemKey}
              disabled
              {...(item.icon ? { icon: item.icon } : {})}
              {...(item.tone ? { tone: item.tone } : {})}
              disabledMessage={
                item.disabledExplanation ?? "This action is unavailable"
              }
            >
              {item.name}
            </Button>
          );
        }

        return (
          <Button
            key={itemKey}
            {...(item.icon ? { icon: item.icon } : {})}
            {...(item.tone ? { tone: item.tone } : {})}
            onClick={item.action as () => void}
          >
            {item.name}
          </Button>
        );
      })}
    </Menu>
  );
}
export default ThreeDotMenu;
